import React from 'react';
import { func, bool, number, arrayOf, object } from 'prop-types';
import { waypointPropTypes } from '../../../proptypes/shared';
import * as styles from './styles/styles.module.scss';
import Button from '../../shared/Button';
import Waypoints from '../Waypoints';
import { ADD_NEW_WAYPOINT_FORM, DELETE_ALL_WAYPOINTS, TOGGLE_SHOW_ALL_TITLES, REORDER_WAYPOINTS } from '../../../hooks/useWaypoints';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { deleteAllWaypoints, updateAllWaypoints, reorderWaypoints } from '../../../utils/requests/at_map_requests';

const WaypointsSection = ({
  waypoints,
  dispatchWaypoints,
  editIndex,
  messagingChannel,
  mapId,
  sortMode,
  canEdit,
  trailId,
  isAdmin,
  createMapWithWaypoint,
  dispatchFormState
}) => {
  let editButtons;
  if (canEdit) {
    let addNewWaypointButton;
    if (editIndex === null) {
      const addWaypoint = () => {
        dispatchWaypoints({ type: ADD_NEW_WAYPOINT_FORM });
      };

      addNewWaypointButton = (
        <div className={styles.newWaypointContainer}>
          <Button variant="secondary" onClick={addWaypoint}>
            {StringConstantsMixin.strings.ADD_NEW_WAYPOINT}
          </Button>
        </div>
      );
    }

    let allWaypointsButtons;
    if (editIndex === null && waypoints.length > 0) {
      const trackDetailStrings = StringConstantsMixin.strings.TRACK_DETAIL;

      const isAllShowTitle = () => {
        // Only allow admins to hide all titles, not show all
        if (trailId) {
          return true;
        }
        return waypoints.every(wp => {
          return wp.waypointDisplayProperty.showTitle;
        });
      };

      const handleAllTitlesToggle = () => {
        if (!canEdit || waypoints.length < 1) {
          return;
        }

        const showTitle = !isAllShowTitle();
        updateAllWaypoints(mapId, { showTitle }).then(() => {
          dispatchWaypoints({ type: TOGGLE_SHOW_ALL_TITLES, payload: showTitle });
        });
      };

      const handleDeleteAll = e => {
        e.preventDefault();

        if (!canEdit || waypoints.length < 1 || !confirm(StringConstantsMixin.strings.TRACK_DETAIL.DELETE_ALL_WP_QUESTION)) {
          return;
        }

        deleteAllWaypoints(mapId).then(() => {
          dispatchWaypoints({ type: DELETE_ALL_WAYPOINTS });
        });
      };

      const handleReverseClick = e => {
        e.preventDefault();

        if (!canEdit || waypoints.length < 1) {
          return;
        }

        const reversedWaypointIds = waypoints
          .map(wp => wp.id)
          .filter(wpId => !!wpId)
          .reverse();
        reorderWaypoints(mapId, reversedWaypointIds).then(reversedWaypoints => {
          dispatchWaypoints({ type: REORDER_WAYPOINTS, payload: reversedWaypoints });
        });
      };

      allWaypointsButtons = (
        <div className={styles.adminButtons}>
          <Button variant="flat" onClick={handleDeleteAll}>
            {StringConstantsMixin.strings.TRACK_DETAIL.DELETE_ALL}
          </Button>
          |
          <Button variant="flat" onClick={handleAllTitlesToggle}>
            {isAllShowTitle() ? trackDetailStrings.HIDE_ALL_WPS : trackDetailStrings.SHOW_ALL_WPS}
          </Button>
          |
          <Button variant="flat" onClick={handleReverseClick}>
            {trackDetailStrings.REVERSE_ORDER}
          </Button>
        </div>
      );
    }

    editButtons = (
      <>
        {addNewWaypointButton}
        {allWaypointsButtons}
      </>
    );
  }

  return (
    <div className={styles.container}>
      <Waypoints
        waypoints={waypoints}
        editIndex={editIndex}
        dispatchWaypoints={dispatchWaypoints}
        messagingChannel={messagingChannel}
        mapId={mapId}
        sortMode={sortMode}
        canEdit={canEdit}
        trailId={trailId}
        isAdmin={isAdmin}
        createMapWithWaypoint={createMapWithWaypoint}
        dispatchFormState={dispatchFormState}
      />
      {editButtons}
    </div>
  );
};

WaypointsSection.propTypes = {
  waypoints: arrayOf(waypointPropTypes),
  dispatchWaypoints: func,
  editIndex: number,
  messagingChannel: object,
  mapId: number,
  sortMode: bool,
  canEdit: bool,
  trailId: number,
  isAdmin: bool,
  createMapWithWaypoint: func,
  dispatchFormState: bool
};

WaypointsSection.defaultProps = {
  trailId: null,
  dispatchWaypoints: () => {},
  waypoints: [],
  editIndex: null,
  messagingChannel: null,
  mapId: null,
  sortMode: false,
  canEdit: false,
  isAdmin: false,
  createMapWithWaypoint: null,
  dispatchFormState: false
};

export default WaypointsSection;
